if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      normalizeGenkidamaDeliveryKey(variant, html) {
        const deliveryText = String(variant?.delivery_time || '').toLowerCase();
        const sourceKey = String(html?.querySelector('#shipping-date-output')?.getAttribute('data-key') || '').toLowerCase();
        const currentKey = String(this.querySelector('#shipping-date-output')?.getAttribute('data-key') || '').toLowerCase();
        const marker = `${deliveryText} ${sourceKey} ${currentKey}`.replace(/\s+/g, ' ');

        if (marker.includes('3month') || marker.includes('45 bis 90') || marker.includes('45-90')) {
          return { key: '3month;;', range: [60, 75], group: 'Seeweg-Gruppe (45-90 Werktage)', pill: 'Seeweg (45-90 Werktage)' };
        }
        if (marker.includes('4-15') || marker.includes('4 - 15')) {
          return { key: '4-15;;', range: [4, 15], group: 'Fernlager-Gruppe (4-15 Tage)', pill: 'Fernlager (4-15 Tage)' };
        }
        if (marker.includes('7-14') || marker.includes('8 - 15') || marker.includes('8-15')) {
          return { key: '7-14;;', range: [7, 14], group: 'Lager-Gruppe (8-15 Werktage)', pill: 'Lager (8-15 Werktage)' };
        }
        if (marker.includes('2-7') || marker.includes('3 - 8') || marker.includes('3-8')) {
          return { key: '2-7;;', range: [2, 7], group: 'Lager-Gruppe (3-8 Werktage)', pill: 'Lager (3-8 Werktage)' };
        }
        if (marker.includes('1-3') || marker.includes('1 - 4') || marker.includes('1-4')) {
          return { key: '1-3;;', range: [1, 3], group: 'Expresslager-Gruppe (1-4 Werktage)', pill: 'Expresslager (1-4 Werktage)' };
        }

        return null;
      }

      isGenkidamaPreorderVariant(variant) {
        const deliveryText = String(variant?.delivery_time || '').toLowerCase();
        return Boolean(variant?.is_preorder || deliveryText.includes('vorbestellung'));
      }

      formatGenkidamaReleaseMonth(value) {
        const match = String(value || '').match(/(0?[1-9]|1[0-2])\.(\d{4})/);
        if (!match) return '';
        const month = match[1].padStart(2, '0');
        const months = {
          '01': 'Januar',
          '02': 'Februar',
          '03': 'Maerz',
          '04': 'April',
          '05': 'Mai',
          '06': 'Juni',
          '07': 'Juli',
          '08': 'August',
          '09': 'September',
          '10': 'Oktober',
          '11': 'November',
          '12': 'Dezember',
        };
        return `${months[month] || month} ${match[2]}`;
      }

      refreshGenkidamaPreorderCard(variant) {
        const release = String(variant?.preorder_release || '').trim();
        const predateCard = this.querySelector('.predate-card');
        const predateValue = predateCard?.querySelector('.predate-date');

        if (!this.isGenkidamaPreorderVariant(variant)) {
          if (predateCard) predateCard.hidden = true;
          return false;
        }

        if (predateCard) predateCard.hidden = false;
        if (predateValue && release) predateValue.textContent = release.replace(/_/g, ' ');
        return true;
      }

      addGenkidamaBusinessDays(startDate, days) {
        const date = new Date(startDate);
        while (days > 0) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) days--;
        }
        return date;
      }

      refreshGenkidamaShipping(variant, html) {
        const output = this.querySelector('#shipping-date-output');
        const groupPill = this.querySelector('#ship-group-pill');
        const groupName = this.querySelector('#group-name');
        const textElem = this.querySelector('#koppel-text');
        const iconElem = this.querySelector('#group-icon');
        const picked = this.normalizeGenkidamaDeliveryKey(variant, html);
        const isPreorder = !picked && this.refreshGenkidamaPreorderCard(variant);
        const releaseLabel = this.formatGenkidamaReleaseMonth(variant?.preorder_release);

        if (isPreorder) {
          const price = Number(variant?.price || 0) / 100;
          const isHighValue = price >= 149.9;
          const groupLabel = `Release-Gruppe${releaseLabel ? ` ${releaseLabel}` : ''}`;

          if (output && variant?.preorder_release) {
            output.textContent = String(variant.preorder_release).replace(/_/g, ' ');
          }

          if (groupPill) groupPill.textContent = 'Vorbestellung';
          if (groupName) groupName.textContent = isHighValue ? `${groupLabel} (Mixbar)` : groupLabel;
          if (iconElem) iconElem.innerHTML = '<span class="genki-card-icon">&#9733;</span>';
          if (textElem) {
            textElem.classList.toggle('is-ok', isHighValue);
            textElem.classList.toggle('is-warn', !isHighValue);
            textElem.style.color = '';
            textElem.innerHTML = isHighValue
              ? '<strong style="color:inherit;">Vorteil:</strong> Dieser Artikel erreicht die 150 &euro; schon allein und darf mit anderen Paketgruppen kombiniert werden. Paketgruppen unter 150 &euro; koennen im Warenkorb per Extra-Versand freigegeben werden. Artikel derselben Gruppe reisen zusammen in einem Paket.'
              : '<strong style="color:inherit;">Regel:</strong> Alles darf zusammen in den Warenkorb. Beim Kombinieren mehrerer Paketgruppen braucht <strong>jede Gruppe mindestens 150 &euro; Warenwert</strong> oder im Warenkorb einen <strong>Extra-Versand fuer dieses Paket</strong> - die Menge zaehlt mit. Wird nur diese eine Release-Gruppe bestellt, gibt es keinen Mindestwert.';
          }
          return;
        }

        if (!picked) {
          return;
        }

        if (output) {
          output.setAttribute('data-key', picked.key);
          let now = new Date();
          if (now.getHours() >= 12) now.setDate(now.getDate() + 1);
          const dateMin = this.addGenkidamaBusinessDays(now, picked.range[0]);
          const dateMax = this.addGenkidamaBusinessDays(now, picked.range[1]);
          const fmt = { day: '2-digit', month: '2-digit', year: 'numeric' };
          output.textContent = `Lieferung ca. ${dateMin.toLocaleDateString('de-DE', fmt)} - ${dateMax.toLocaleDateString('de-DE', fmt)}`;
        }

        if (groupPill) groupPill.textContent = picked.pill;

        const price = Number(variant?.price || 0) / 100;
        const isHighValue = price >= 149.9;
        if (groupName) groupName.textContent = isHighValue ? `${picked.group} (Mixbar)` : picked.group;

        if (iconElem) {
          iconElem.innerHTML = isHighValue
            ? '<span class="genki-card-icon">&#9733;</span>'
            : '<span class="genki-card-icon">&#9888;</span>';
        }

        if (textElem) {
          textElem.classList.toggle('is-ok', isHighValue);
          textElem.classList.toggle('is-warn', !isHighValue);
          textElem.style.color = '';
          textElem.innerHTML = isHighValue
            ? '<strong style="color:inherit;">Vorteil:</strong> Dieser Artikel erreicht die 150 &euro; schon allein und darf mit anderen Paketgruppen kombiniert werden. Paketgruppen unter 150 &euro; koennen im Warenkorb per Extra-Versand freigegeben werden. Artikel derselben Gruppe reisen zusammen in einem Paket.'
            : '<strong style="color:inherit;">Regel:</strong> Alles darf zusammen in den Warenkorb. Beim Kombinieren mehrerer Paketgruppen braucht <strong>jede Gruppe mindestens 150 &euro; Warenwert</strong> oder im Warenkorb einen <strong>Extra-Versand fuer dieses Paket</strong> - die Menge zaehlt mit. Wird nur diese eine Gruppe bestellt, gibt es keinen Mindestwert.';
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.productForm?.updatePurchaseSnapshot?.(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, this.getVariantFeaturedMediaId(variant, html));

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          const updatePlainSourceFromDestination = (selector, shouldHide = (source) => false) => {
            const source = html.querySelector(selector);
            const destination = this.querySelector(selector);
            if (source && destination) {
              Array.from(destination.attributes).forEach((attribute) => destination.removeAttribute(attribute.name));
              Array.from(source.attributes).forEach((attribute) => destination.setAttribute(attribute.name, attribute.value));
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updatePlainSourceFromDestination('[data-genki-wishlist-button]');
          updatePlainSourceFromDestination('#shipping-group-info');
          updatePlainSourceFromDestination('#shipping-date-output');
          updatePlainSourceFromDestination('#versandzeit-anzeige');
          updatePlainSourceFromDestination('[data-genkidama-variant-refresh]');
          this.refreshGenkidamaShipping(variant, html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      getVariantFeaturedMediaId(variant, html) {
        const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
        if (mediaId) return mediaId;

        const activeMedia = html.querySelector('media-gallery li.is-active[data-media-id]');
        return activeMedia?.dataset?.mediaId?.split('-').pop() || null;
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { preventScroll: true }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
